/**
 * Diagram Templates
 *
 * The starting scenes offered in the new-diagram dialog (Requirement 8.6).
 * v1 ships two: "Blank" (an empty canvas) and "Architecture overview" (the
 * team's usual layout, copied verbatim from
 * `assets/architecture-overview.excalidraw` into `architecture-overview.json`;
 * it is not regenerated or restyled here).
 *
 * `applyTemplateTitle` replaces the Architecture overview's placeholder title
 * text with the new Diagram_Title before the scene is exported and committed.
 */

import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types"
import type { DiagramScene } from "@/components/diagrams/diagramSvg"
import architectureOverviewJson from "@/components/diagrams/templates/architecture-overview.json"

/**
 * The placeholder text in a template that `applyTemplateTitle` replaces with
 * the real Diagram_Title. The Architecture overview's title text element holds
 * exactly this string.
 */
export const TEMPLATE_TITLE_PLACEHOLDER: string = "diagram title"

/**
 * A single text element of an Excalidraw scene. Narrowed from the template
 * JSON (typed `unknown` by `resolveJsonModule`) to just the fields
 * `applyTemplateTitle` reads and rewrites.
 */
interface TextSceneElement {
  /** The element type discriminator; "text" for title/label elements. */
  type: string
  /** The displayed text (what the user sees). */
  text?: string
  /** The source text Excalidraw re-wraps from; kept in step with `text`. */
  originalText?: string
}

/**
 * The shape of a bundled template JSON file: a full Excalidraw scene export.
 * `files` is optional because an empty export may omit it.
 */
interface TemplateScene {
  /** The scene's elements. */
  elements: readonly ExcalidrawElement[]
  /** The scene's app state. */
  appState: Partial<DiagramScene["appState"]>
  /** The scene's binary files, or undefined when the export omitted them. */
  files?: DiagramScene["files"]
}

/**
 * A template available in the new-diagram dialog.
 */
export interface DiagramTemplate {
  /** A stable key for the template (used in UI state and tests). */
  id: string
  /** The human-readable name shown in the dialog. */
  name: string
  /**
   * Build a fresh scene for this template. Returns a new object each call so a
   * caller mutating the scene (e.g. after `applyTemplateTitle`) never affects
   * the bundled template or other new diagrams.
   */
  buildScene: () => DiagramScene
}

/**
 * Build an empty scene for the "Blank" template: no elements, no files, and an
 * empty app state (the editor layers the house style over it on load).
 *
 * @returns A new empty DiagramScene.
 */
function buildBlankScene(): DiagramScene {
  return {
    elements: [],
    appState: {},
    files: {},
  }
}

/**
 * Build the "Architecture overview" scene from the bundled template JSON.
 *
 * Deep-clones the imported JSON so the returned scene can be mutated (for
 * example by `applyTemplateTitle`) without changing the module-level import,
 * which would otherwise leak the previous diagram's title into the next one.
 *
 * @returns A new DiagramScene cloned from the architecture template.
 */
function buildArchitectureScene(): DiagramScene {
  // The import is typed structurally by `resolveJsonModule` and does not carry
  // Excalidraw's internal element fields (`index`, etc.); cast through `unknown`
  // since the file is a real Excalidraw export verified at build and runtime.
  const template: TemplateScene = architectureOverviewJson as unknown as TemplateScene
  // structuredClone is available in all supported browsers and in the Node 22
  // test runner; it deep-copies the scene so edits never touch the import.
  const cloned: TemplateScene = structuredClone(template)
  return {
    elements: cloned.elements,
    appState: cloned.appState,
    files: cloned.files ?? {},
  }
}

/**
 * The templates offered in the new-diagram dialog, in display order: Blank
 * first, then Architecture overview.
 */
export const DIAGRAM_TEMPLATES: readonly DiagramTemplate[] = [
  {
    id: "blank",
    name: "Blank",
    buildScene: buildBlankScene,
  },
  {
    id: "architecture-overview",
    name: "Architecture overview",
    buildScene: buildArchitectureScene,
  },
]

/**
 * Return a copy of `scene` with the template's placeholder title text replaced
 * by `title`.
 *
 * Finds the first text element whose text is the placeholder
 * (`TEMPLATE_TITLE_PLACEHOLDER`) and rewrites both its `text` and `originalText`
 * so Excalidraw re-wraps and renders the new title. Elements are copied rather
 * than mutated in place, so the caller's input scene is left unchanged. A scene
 * with no such placeholder (e.g. the Blank template) is returned unchanged.
 *
 * @param scene - The template scene to apply the title to.
 * @param title - The Diagram_Title to insert in place of the placeholder.
 * @returns A new DiagramScene with the title applied.
 */
export function applyTemplateTitle(scene: DiagramScene, title: string): DiagramScene {
  let replaced: boolean = false
  const elements: ExcalidrawElement[] = scene.elements.map(
    (element: ExcalidrawElement): ExcalidrawElement => {
      // Only the first placeholder is replaced; later text is left alone.
      if (replaced) {
        return element
      }
      const textElement = element as unknown as TextSceneElement
      if (textElement.type !== "text" || textElement.text !== TEMPLATE_TITLE_PLACEHOLDER) {
        return element
      }
      replaced = true
      // Spread a shallow copy and overwrite both text fields; keeping them in
      // step is what makes Excalidraw show the new title.
      return {
        ...element,
        text: title,
        originalText: title,
      } as unknown as ExcalidrawElement
    },
  )

  return {
    elements,
    appState: scene.appState,
    files: scene.files,
  }
}
